import axiosInstance from '../../../axios';
import React, { useCallback, useState } from 'react';
import { getErrorMessage } from '../../../utils/request';

const CreateQuestionGroupContext = React.createContext();

function CreateQuestionGroupProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const createQuestionGroup = useCallback(async (payload) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('/question-groups', payload);

      return {
        questionGroup: getQuestionGroupFromResponse(response),
        success: true,
        error: null,
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setErrorMessage(errorMessage);
      return { success: false, errorMessage };
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, []);

  const value = {
    loading,
    createQuestionGroup,
    hasLoaded,
    errorMessage,
  };

  return (
    <CreateQuestionGroupContext.Provider value={value}>
      {children}
    </CreateQuestionGroupContext.Provider>
  );
}

/**
 * @typedef {{
 *  id: string;
 *  name: string;
 * }} QuestionGroup
 */

/**
 * @returns {{
 *  loading: boolean,
 *  createQuestionGroup: (questionGroup: QuestionGroup) => Promise<{ success: boolean; errorMessage: string; }>
 *  hasLoaded: boolean,
 *  errorMessage: string
 * }}
 */

function useCreateQuestionGroup() {
  const context = React.useContext(CreateQuestionGroupContext);

  if (context === undefined) {
    throw new Error(
      'useCreateQuestionGroup must be used within a CreateQuestionGroupProvider',
    );
  }

  return context;
}

function getQuestionGroupFromResponse(response) {
  if (!response.data) {
    return null;
  }

  const questionGroup = response.data;
  return {
    id: questionGroup.id,
    name: questionGroup.name,
  };
}

export { CreateQuestionGroupProvider, useCreateQuestionGroup };
